// Artifact document schema. Closed vocabulary: anything the IR cannot express becomes a new
// node type here, never an escape hatch. Text is always rendered as text.
import * as s from './schema-dsl.ts'
import { INTENTS, PRESET_DESCRIPTION, SEVERITIES, presetIds, type Intent, type Severity } from './tokens.ts'
import { GAPS, SPLIT_RATIOS, MIN_WIDTHS, ALIGNS, MAX_GRID_COLUMNS, type Gap, type SplitRatio, type MinWidth, type Align } from './layout.ts'
import { VALUE_FORMATS, type ValueFormat } from './format.ts'
import { RULE_OPS, type Rule } from './rules.ts'
import { chartSchema, CHART_KINDS } from './charts.ts'
import './charts-advanced.ts'

export const DOCUMENT_VERSION = 1
export const DOCUMENT_KINDS = ['seo_audit', 'growth_audit', 'audit', 'report', 'dashboard', 'health', 'custom'] as const
export type DocumentKind = typeof DOCUMENT_KINDS[number]

export const MAX_DOCUMENT_BYTES = 1_000_000
export const MAX_NODES = 2500
export const MAX_DEPTH = 12

export const MOTIONS = ['none', 'fade', 'rise', 'stagger', 'count-up'] as const
export type Motion = typeof MOTIONS[number]

export const ENTITY_KINDS = ['shop', 'product', 'category', 'collection', 'resource', 'page', 'file', 'url', 'setting'] as const
export type EntityKind = typeof ENTITY_KINDS[number]

// No regex flags anywhere in schema patterns: JSON Schema (and PHP's validator) cannot carry them.
export const HTTPS_URL = /^https:\/\/[A-Za-z0-9.-]+(:\d{1,5})?(\/[^\s"'<>\\`]*)?$/
const ID_RE = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/
const RULE_ID_RE = /^[A-Za-z0-9_.-]{1,64}$/
export const COLUMN_KEY_RE = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/
export const LOCALE_RE = /^[a-z]{2}_[A-Z]{2}$/

// ---------------------------------------------------------------------------
// Types

export interface NodeBase {
  id?: string
  span?: number | 'full'
  motion?: Motion
}

export interface HeadingNode extends NodeBase { type: 'heading', text: string, level?: 1 | 2 | 3, eyebrow?: string }
export interface TextNode extends NodeBase { type: 'text', text: string, tone?: 'default' | 'lead' | 'muted' | 'small' }
export interface ListNode extends NodeBase { type: 'list', items: string[], ordered?: boolean }
export interface CalloutNode extends NodeBase { type: 'callout', intent?: Intent, title?: string, text: string }
export interface DividerNode extends NodeBase { type: 'divider' }
export interface MetricDelta { value: number, format?: ValueFormat, goodDirection?: 'up' | 'down', label?: string }
export interface MetricNode extends NodeBase {
  type: 'metric', label: string, value: number | string | null, format?: ValueFormat, currency?: string,
  delta?: MetricDelta, intent?: Intent, caption?: string, rules?: Rule[], sparkline?: (number | null)[]
}
export interface ScoreNode extends NodeBase { type: 'score', value: number, label: string, caption?: string, intent?: Intent }
export interface EntityRef { kind: EntityKind, id?: string | number, label: string, url?: string }
export interface FindingItem {
  severity: Severity, title: string, detail?: string, fix?: string, entity?: EntityRef,
  audience?: 'merchant' | 'platform', ruleId?: string, effort?: 'quick' | 'moderate' | 'involved',
  impact?: 'high' | 'medium' | 'low', count?: number
}
export interface FindingNode extends NodeBase, FindingItem { type: 'finding' }
export interface FindingsNode extends NodeBase { type: 'findings', items: FindingItem[], groupBy?: 'severity' | 'none', title?: string }
export interface TableColumn { key: string, label: string, format?: ValueFormat, currency?: string, align?: 'start' | 'center' | 'end', width?: 'auto' | 'narrow' | 'wide' }
export interface TableNode extends NodeBase {
  type: 'table', columns: TableColumn[], rows: Record<string, string | number | boolean | null>[],
  rules?: Rule[], caption?: string, maxRows?: number, title?: string
}
export interface ChartNode extends NodeBase { type: 'chart', chart: { kind: string } & Record<string, unknown>, title?: string, caption?: string, height?: 'sm' | 'md' | 'lg' }
export interface LinkNode extends NodeBase { type: 'link', label: string, href: string, style?: 'inline' | 'button' }
export interface DiagramNode extends NodeBase { type: 'diagram', syntax: 'mermaid', source: string, title?: string, caption?: string }
export interface SectionNode extends NodeBase {
  type: 'section', title?: string, description?: string, eyebrow?: string,
  surface?: 'panel' | 'plain' | 'sunken', children: MaxNode[]
}
export interface GridNode extends NodeBase { type: 'grid', columns?: number | 'auto', minWidth?: MinWidth, gap?: Gap, align?: Align, children: MaxNode[] }
export interface StackNode extends NodeBase { type: 'stack', gap?: Gap, align?: Align, direction?: 'vertical' | 'horizontal', wrap?: boolean, children: MaxNode[] }
export interface SplitNode extends NodeBase { type: 'split', ratio?: SplitRatio, gap?: Gap, align?: Align, children: MaxNode[] }
/** Each child section is one tab; its title is the tab label */
export interface TabsNode extends NodeBase { type: 'tabs', label?: string, children: (SectionNode & { title: string })[] }
export interface RequestNode extends NodeBase { type: 'request', text: string, title?: string, caption?: string }

export type MaxNode =
  | HeadingNode | TextNode | ListNode | CalloutNode | DividerNode | MetricNode | ScoreNode
  | FindingNode | FindingsNode | TableNode | ChartNode | LinkNode | DiagramNode | RequestNode
  | SectionNode | GridNode | StackNode | SplitNode | TabsNode

export interface MaxDocument {
  version: 1
  kind: DocumentKind
  title: string
  subtitle?: string
  preset?: string
  locale: string
  generatedAt?: string
  period?: { from?: string, to?: string, label?: string }
  body: MaxNode[]
}

export const CONTAINER_TYPES = ['section', 'grid', 'stack', 'split', 'tabs'] as const
export const MAX_TABS = 6

// ---------------------------------------------------------------------------
// Schema

const intent = s.enumOf(INTENTS)
const severity = s.enumOf(SEVERITIES)
const valueFormat = s.enumOf(VALUE_FORMATS)
const currency = s.string({ min: 3, max: 3, pattern: /^[A-Z]{3}$/ })
const httpsUrl = s.string({ max: 2000, pattern: HTTPS_URL, description: 'Absolute https URL' })
const scalar = s.anyOf([s.string({ max: 500 }), s.number(), s.boolean(), s.nullValue()])

const nodeBase = {
  id: s.string({ max: 64, pattern: ID_RE }),
  span: s.anyOf([s.number({ min: 1, max: MAX_GRID_COLUMNS, integer: true }), s.literal('full')], 'Columns to span inside a grid'),
  motion: s.enumOf(MOTIONS, 'Entrance animation; always disabled for users who prefer reduced motion')
}

export const ruleSchema = s.object({
  field: s.string({ max: 64, pattern: COLUMN_KEY_RE }),
  op: s.enumOf(RULE_OPS),
  value: s.anyOf([scalar, s.array(scalar, { max: 50 })]),
  intent,
  scope: s.enumOf(['cell', 'row']),
  label: s.string({ max: 80 })
}, {
  required: ['field', 'op', 'intent'],
  refine (v, _p, report) {
    const needsValue = !['empty', 'notEmpty'].includes(v.op as string)
    if (needsValue && v.value === undefined) report('value', 'is required for this op')
    if (['in', 'notIn'].includes(v.op as string) && !Array.isArray(v.value)) report('value', 'must be an array for in/notIn')
    if (v.op === 'between' && (!Array.isArray(v.value) || v.value.length !== 2)) report('value', 'must be [low, high] for between')
  }
})

const entityRef = s.object({
  kind: s.enumOf(ENTITY_KINDS),
  id: s.anyOf([s.string({ max: 64 }), s.number({ integer: true })]),
  label: s.string({ max: 160 }),
  url: httpsUrl
}, { required: ['kind', 'label'] })

const findingProps = {
  severity,
  title: s.string({ max: 160 }),
  detail: s.string({ max: 1200 }),
  fix: s.string({ max: 800, description: 'Concrete action the merchant can take' }),
  entity: entityRef,
  audience: s.enumOf(['merchant', 'platform'], 'platform findings are Johku defects and are hidden from merchants'),
  ruleId: s.string({ max: 64, pattern: RULE_ID_RE }),
  effort: s.enumOf(['quick', 'moderate', 'involved']),
  impact: s.enumOf(['high', 'medium', 'low']),
  count: s.number({ min: 0, integer: true, description: 'Number of affected items when the finding groups several' })
}

const findingItem = s.object(findingProps, { required: ['severity', 'title'] })

const children = s.array(s.ref('Node'), { max: 200 })

export function nodeSchema (type: string, props: Record<string, s.Schema>, required: string[] = [], refine?: s.ObjectOpts['refine'], extra: Record<string, s.Schema> = {}) {
  return s.object({ type: s.literal(type), ...nodeBase, ...extra, ...props }, { required: ['type', ...required], refine })
}

/**
 * Node variants keyed by type. Containers reference the `Node` definition of whichever
 * validator uses them, so specs reuse these with their own `Node` and extra base props.
 */
export function buildNodeVariants (extra: Record<string, s.Schema> = {}): Record<string, s.Schema> {
  const node = (type: string, props: Record<string, s.Schema>, required: string[] = [], refine?: s.ObjectOpts['refine']) =>
    nodeSchema(type, props, required, refine, extra)
  const sectionProps = {
    title: s.string({ max: 160 }),
    description: s.string({ max: 600 }),
    eyebrow: s.string({ max: 60 }),
    surface: s.enumOf(['panel', 'plain', 'sunken']),
    children
  }
  return {
    heading: node('heading', {
      text: s.string({ max: 200 }),
      level: s.enumOf([1, 2, 3]),
      eyebrow: s.string({ max: 60 })
    }, ['text']),
    text: node('text', {
      text: s.string({ max: 6000, description: 'Plain text; blank lines separate paragraphs. Never HTML or Markdown.' }),
      tone: s.enumOf(['default', 'lead', 'muted', 'small'])
    }, ['text']),
    list: node('list', {
      items: s.array(s.string({ max: 500 }), { min: 1, max: 50 }),
      ordered: s.boolean()
    }, ['items']),
    callout: node('callout', {
      intent,
      title: s.string({ max: 120 }),
      text: s.string({ max: 1500 })
    }, ['text']),
    divider: node('divider', {}),
    metric: node('metric', {
      label: s.string({ max: 80 }),
      value: s.anyOf([s.number(), s.string({ max: 40 }), s.nullValue()]),
      format: valueFormat,
      currency,
      delta: s.object({
        value: s.number(),
        format: valueFormat,
        goodDirection: s.enumOf(['up', 'down']),
        label: s.string({ max: 60 })
      }, { required: ['value'] }),
      intent,
      caption: s.string({ max: 160 }),
      rules: s.array(ruleSchema, { max: 10, description: 'Threshold rules evaluated against field "value"' }),
      sparkline: s.array(s.anyOf([s.number(), s.nullValue()]), { max: 120 })
    }, ['label', 'value']),
    score: node('score', {
      value: s.number({ min: 0, max: 100 }),
      label: s.string({ max: 80 }),
      caption: s.string({ max: 240 }),
      intent
    }, ['value', 'label']),
    finding: node('finding', findingProps, ['severity', 'title']),
    findings: node('findings', {
      items: s.array(findingItem, { max: 500 }),
      groupBy: s.enumOf(['severity', 'none']),
      title: s.string({ max: 120 })
    }, ['items']),
    table: node('table', {
      title: s.string({ max: 120 }),
      columns: s.array(s.object({
        key: s.string({ max: 64, pattern: COLUMN_KEY_RE }),
        label: s.string({ max: 80 }),
        format: valueFormat,
        currency,
        align: s.enumOf(['start', 'center', 'end']),
        width: s.enumOf(['auto', 'narrow', 'wide'])
      }, { required: ['key', 'label'] }), { min: 1, max: 24 }),
      rows: s.array(s.record(scalar, { maxKeys: 24, keyPattern: COLUMN_KEY_RE }), { max: 2000 }),
      rules: s.array(ruleSchema, { max: 20 }),
      caption: s.string({ max: 300 }),
      maxRows: s.number({ min: 1, max: 500, integer: true })
    }, ['columns', 'rows'], (v, _p, report) => {
      const keys = new Set((v.columns as { key: string }[]).map(c => c.key))
      if (keys.size !== (v.columns as unknown[]).length) report('columns', 'column keys must be unique')
      ;((v.rules as { field: string }[] | undefined) ?? []).forEach((r, i) => {
        if (!keys.has(r.field)) report(`rules[${i}].field`, `is not a column key`)
      })
    }),
    chart: node('chart', {
      chart: chartSchema(),
      title: s.string({ max: 120 }),
      caption: s.string({ max: 300 }),
      height: s.enumOf(['sm', 'md', 'lg'])
    }, ['chart']),
    link: node('link', {
      label: s.string({ max: 120 }),
      href: httpsUrl,
      style: s.enumOf(['inline', 'button'])
    }, ['label', 'href']),
    diagram: node('diagram', {
      syntax: s.literal('mermaid'),
      source: s.string({ max: 6000, description: 'Mermaid flowchart, sequence, state, timeline, journey, pie, gantt, mindmap or quadrant source. Click directives and HTML labels are stripped.' }),
      title: s.string({ max: 120 }),
      caption: s.string({ max: 300 })
    }, ['syntax', 'source']),
    request: node('request', {
      text: s.string({ max: 1000, description: 'A ready-made request the reader copies and gives to Johku Max, in the document language' }),
      title: s.string({ max: 120 }),
      caption: s.string({ max: 240 })
    }, ['text']),
    section: node('section', sectionProps, ['children']),
    tabs: node('tabs', {
      label: s.string({ max: 80, description: 'Names the tab list for screen readers' }),
      children: s.array(node('section', sectionProps, ['children', 'title']), {
        min: 2, max: MAX_TABS, description: 'One section per tab; the section title is the tab label. Print and PDF show every tab.'
      })
    }, ['children']),
    grid: node('grid', {
      columns: s.anyOf([s.number({ min: 1, max: MAX_GRID_COLUMNS, integer: true }), s.literal('auto')]),
      minWidth: s.enumOf(MIN_WIDTHS),
      gap: s.enumOf(GAPS),
      align: s.enumOf(ALIGNS),
      children
    }, ['children']),
    stack: node('stack', {
      gap: s.enumOf(GAPS),
      align: s.enumOf(ALIGNS),
      direction: s.enumOf(['vertical', 'horizontal']),
      wrap: s.boolean(),
      children
    }, ['children']),
    split: node('split', {
      ratio: s.enumOf(SPLIT_RATIOS),
      gap: s.enumOf(GAPS),
      align: s.enumOf(ALIGNS),
      children: s.array(s.ref('Node'), { min: 2, max: 2 })
    }, ['children'])
  }
}

let cached: { validator: s.Validator, chartKinds: string } | null = null

export function documentValidator (): s.Validator {
  const kinds = Object.keys(CHART_KINDS).join(',')
  if (cached && cached.chartKinds === kinds) return cached.validator
  const node = s.oneOfBy('type', buildNodeVariants(), 'Document node')
  const root = s.object({
    version: s.literal(DOCUMENT_VERSION),
    kind: s.enumOf(DOCUMENT_KINDS),
    title: s.string({ min: 1, max: 160 }),
    subtitle: s.string({ max: 300 }),
    preset: s.enumOf(presetIds(), PRESET_DESCRIPTION),
    locale: s.string({ max: 5, pattern: LOCALE_RE, description: 'Language the prose is written in, e.g. fi_FI' }),
    generatedAt: s.string({ max: 40, pattern: /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/ }),
    period: s.object({
      from: s.string({ max: 40 }),
      to: s.string({ max: 40 }),
      label: s.string({ max: 80 })
    }),
    body: s.array(s.ref('Node'), { min: 1, max: 200 })
  }, { required: ['version', 'kind', 'title', 'locale', 'body'] })
  const validator: s.Validator = { root, defs: { Node: node }, maxDepth: MAX_DEPTH }
  cached = { validator, chartKinds: kinds }
  return validator
}

export function documentJsonSchema (): Record<string, unknown> {
  return {
    ...s.toJsonSchema(documentValidator(), { id: 'https://schemas.johku.com/max/document.v1.json', title: 'Johku Max document' }),
    'x-max-bytes': MAX_DOCUMENT_BYTES,
    'x-max-nodes': MAX_NODES
  }
}

export function countNodes (nodes: unknown): number {
  if (!Array.isArray(nodes)) return 0
  let n = 0
  for (const node of nodes) {
    n++
    if (node && typeof node === 'object' && Array.isArray((node as { children?: unknown }).children)) {
      n += countNodes((node as { children: unknown[] }).children)
    }
    if (node && typeof node === 'object' && (node as { type?: string }).type === 'findings') {
      n += ((node as { items?: unknown[] }).items?.length ?? 0)
    }
  }
  return n
}

export function validateDocument (doc: unknown): s.ValidationResult {
  const bytes = new TextEncoder().encode(JSON.stringify(doc ?? null)).length
  if (bytes > MAX_DOCUMENT_BYTES) {
    return { valid: false, issues: [{ path: '$', message: `document is ${bytes} bytes; the limit is ${MAX_DOCUMENT_BYTES}` }] }
  }
  const result = s.validateWith(documentValidator(), doc)
  if (result.valid) {
    const nodes = countNodes((doc as MaxDocument).body)
    if (nodes > MAX_NODES) {
      return { valid: false, issues: [{ path: 'body', message: `document has ${nodes} nodes; the limit is ${MAX_NODES}` }] }
    }
  }
  return result
}

export function validateNode (node: unknown): s.ValidationResult {
  const v = documentValidator()
  return s.validateWith({ root: s.ref('Node'), defs: v.defs, maxDepth: MAX_DEPTH }, node, 10)
}

export interface DegradedNode { type: '__degraded', reason: string, originalType?: string }

/**
 * Validation on load: a stale or damaged node degrades to a placeholder instead of failing
 * the whole artifact. Containers keep their valid children.
 */
export function sanitizeDocument (doc: unknown): { doc: MaxDocument | null, degraded: number, issues: s.Issue[] } {
  const full = validateDocument(doc)
  if (full.valid) return { doc: doc as MaxDocument, degraded: 0, issues: [] }
  if (!doc || typeof doc !== 'object' || !Array.isArray((doc as MaxDocument).body)) {
    return { doc: null, degraded: 0, issues: full.issues }
  }
  let degraded = 0
  const issues: s.Issue[] = []
  const fix = (nodes: unknown[], depth: number): unknown[] => nodes.map(node => {
    const type = node && typeof node === 'object' ? (node as { type?: unknown }).type : undefined
    if (depth < MAX_DEPTH && typeof type === 'string' && (CONTAINER_TYPES as readonly string[]).includes(type) && Array.isArray((node as SectionNode).children)) {
      const placeholders = type === 'split'
        ? [{ type: 'divider' }, { type: 'divider' }]
        : type === 'tabs' ? [{ type: 'section', title: '-', children: [] }, { type: 'section', title: '-', children: [] }] : []
      const shell = { ...(node as SectionNode), children: placeholders }
      if (validateNode(shell).valid) {
        if (type === 'tabs') {
          const tabs = (node as SectionNode).children.map((tab, i) => {
            const title = tab && typeof tab === 'object' && typeof (tab as SectionNode).title === 'string' && (tab as SectionNode).title ? (tab as SectionNode).title! : String(i + 1)
            const content = tab && typeof tab === 'object' && Array.isArray((tab as SectionNode).children) ? (tab as SectionNode).children : [tab]
            const shellTab = { ...(tab as SectionNode), type: 'section', title, children: [] }
            const base = validateNode(shellTab).valid ? shellTab : { type: 'section', title, children: [] }
            return { ...base, children: fix(content, depth + 2) as MaxNode[] }
          })
          if (tabs.length > MAX_TABS) tabs.length = MAX_TABS
          return { ...(node as TabsNode), children: tabs } as TabsNode
        }
        return { ...(node as SectionNode), children: fix((node as SectionNode).children, depth + 1) as MaxNode[] }
      }
    }
    const result = validateNode(node)
    if (result.valid) return node
    degraded++
    issues.push(...result.issues.slice(0, 3))
    return { type: '__degraded', reason: result.issues[0]?.message ?? 'invalid', ...(typeof type === 'string' ? { originalType: type } : {}) } satisfies DegradedNode
  })
  const header = { ...(doc as MaxDocument), body: [{ type: 'divider' }] }
  const headerResult = validateDocument(header)
  if (!headerResult.valid) return { doc: null, degraded: 0, issues: headerResult.issues }
  return { doc: { ...(doc as MaxDocument), body: fix((doc as MaxDocument).body, 1) as MaxNode[] }, degraded, issues }
}
